import { ErrorHandler, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * ErrorHandler global de la app.
 *
 * Detecta fallos de carga de chunks lazy (ChunkLoadError), típicos cuando una
 * pestaña lleva abierta durante un deploy y pide un chunk con hash antiguo que
 * ya no existe en el servidor → recarga la página para traer el build nuevo.
 * El resto de errores pasan por el ErrorHandler estándar de Angular (console.error)
 * y, solo si el entorno define `sentryDsn`, se reenvían a Sentry cargándolo bajo
 * demanda (HRS-3045: el SDK ya no va en el bundle inicial).
 *
 * Guard anti-bucle: si vuelve a fallar dentro de una ventana corta tras recargar,
 * no se recarga otra vez (se trata como un error normal), para no entrar en reload
 * infinito.
 */
@Injectable()
export class ChunkErrorHandler extends ErrorHandler {
  private static readonly RELOAD_KEY = 'chunkReloadAt';
  private static readonly RELOAD_WINDOW_MS = 10_000;

  override handleError(error: any): void {
    if (this.isChunkLoadError(error) && this.canReload()) {
      sessionStorage.setItem(ChunkErrorHandler.RELOAD_KEY, String(Date.now()));
      window.location.reload();
      return;
    }

    super.handleError(error);

    if (environment.sentryDsn) {
      import('@sentry/angular')
        .then((Sentry) => Sentry.captureException(error))
        .catch(() => undefined);
    }
  }

  private isChunkLoadError(error: any): boolean {
    const message = error?.message ?? '';
    return (
      error?.name === 'ChunkLoadError' ||
      /Loading chunk [^\s]+ failed/i.test(message) ||
      /Failed to fetch dynamically imported module/i.test(message)
    );
  }

  private canReload(): boolean {
    try {
      const last = Number(sessionStorage.getItem(ChunkErrorHandler.RELOAD_KEY) ?? 0);
      return Date.now() - last > ChunkErrorHandler.RELOAD_WINDOW_MS;
    } catch {
      return true; // sin sessionStorage (modo restringido): recargar igual
    }
  }
}
